import clsx from 'clsx';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    listUsersInEdits,
    type RevisionInfo,
    type RevisionSemanticChange,
    RevisionSemanticChangeType,
    RevisionType,
} from '@gitbook/hive-core';
import { BadgeIcon, type BadgeKind, sizes } from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';

import { GitSyncProviderIcon } from '../GitSyncProviders';
import { UsersGroupAvatarByIds } from '../Users';

/**
 * Icon with the user avatar and a badge.
 */
export function HiveRevisionIcon(props: {
    space: api.Space;
    changeRequest: boolean;
    revision: RevisionInfo;
    changes?: RevisionSemanticChange[];
    className?: string;
}): React.ReactElement {
    const { space, changeRequest, revision, changes = [], className } = props;

    const size = sizes.AVATAR_M;
    const userIconSize = sizes.AVATAR_XS;

    const contributors = listUsersInEdits(revision.edits);
    const hasMultipleContributors = contributors.length > 1;

    return (
        <div className={clsx('relative flex', className)}>
            <BadgeIcon
                size={size}
                {...getBadgeProps(space, changeRequest, revision, changes)}
                className="shrink-0"
            />
            <div
                className={`-right-3 absolute top-[6px] rounded-3xl border-${hasMultipleContributors ? 0 : 1}`}
            >
                <UsersGroupAvatarByIds
                    userIds={contributors}
                    size={userIconSize}
                    alwaysAsGroup={false}
                />
            </div>
        </div>
    );
}

function getBadgeProps(
    space: api.Space,
    changeRequest: boolean,
    revision: RevisionInfo,
    changes: RevisionSemanticChange[]
): { icon: SVGIconComponent; kind: BadgeKind } {
    if (revision.type === RevisionType.Rollback) {
        return {
            icon: Icon.RollBack,
            kind: 'merge',
        };
    }

    if (revision.type === RevisionType.Merge) {
        return {
            icon: Icon.ChangeRequestMerged,
            kind: 'merge',
        };
    }

    if (revision.type === RevisionType.Update) {
        return {
            icon: Icon.ChangeRequestMerged,
            kind: 'merge',
        };
    }

    if (revision.git?.commit && !revision.git.commit.createdByGitBook) {
        return {
            icon: space.gitSync?.installationProvider
                ? GitSyncProviderIcon[space.gitSync.installationProvider]
                : Icon.ChangeRequestMerged,
            kind: 'secondary',
        };
    }

    if (changes?.length === 0) {
        // Initial revision of the space or change request
        return {
            icon: changeRequest ? Icon.ChangeRequest : Icon.Space,
            kind: 'info',
        };
    }

    const [change] = changes;

    if (!change) {
        return {
            icon: Icon.Edit,
            kind: 'info',
        };
    }

    switch (change.type) {
        case RevisionSemanticChangeType.PageEdited:
        case RevisionSemanticChangeType.PageCreated:
        case RevisionSemanticChangeType.PageDeleted:
            return {
                icon: Icon.Page,
                kind: 'info',
            };
        case RevisionSemanticChangeType.PageMoved:
            return {
                icon: Icon.ArrowRight,
                kind: 'info',
            };
        case RevisionSemanticChangeType.FileCreated:
        case RevisionSemanticChangeType.FileEdited:
        case RevisionSemanticChangeType.FileDeleted:
            return {
                icon: Icon.Files,
                kind: 'info',
            };
        default:
            return {
                icon: Icon.Edit,
                kind: 'info',
            };
    }
}
